import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomisableRibbon from 'src/components/customisable-ribbon/CustomisableRibbon';
import ExportButton from 'src/components/export-button';
import { useRibbonAndExport } from 'src/hooks/useRibbonAndExport';
import { PageLayout } from 'src/layouts';

import { useGetCauseRegister } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { getCounter } from '@/utils/collectionUtils';

import CauseModal from '../issues/update/tabs/causes/CauseModal';
import { CauseRegisterToolbar } from './CauseRegisterToolbar';
import { useGetRegisterTableProps } from './config';
import type { CauseFlatField, CauseRegisterFields } from './types';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const Page: FC = () => {
  const [openCause, setOpenCause] = useState<CauseFlatField | null>(null);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'causes',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const { data, loading, refetch } = useGetCauseRegister({ queryArgs: {} });

  const tableProps = useGetRegisterTableProps(
    data?.cause,
    (consequence: CauseRegisterFields) => setOpenCause(consequence)
  );

  const title = st('register_title');
  const counter = getCounter(tableProps.totalItemsCount, loading);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  return (
    <PageLayout
      helpTranslationKey={'causes.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-160) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <CauseRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.Cause}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={loading} />
      {openCause && (
        <CauseModal
          causeId={openCause.Id}
          onDismiss={(saved) => {
            setOpenCause(null);
            if (saved) {
              refetch();
            }
          }}
          issueId={openCause.ParentIssueId}
        />
      )}
    </PageLayout>
  );
};

export default Page;
